import { useEffect, useState } from 'react'

export interface Product {
  title: string
  editionLabel: string
  priceNumber: number
  priceText: string
  thumbnailUrl: string
}

const FALLBACK_PRODUCTS: Product[] = [
  {
    title: 'Pack Despertar',
    editionLabel: 'Physical Book',
    priceNumber: 27,
    priceText: '$27',
    thumbnailUrl: '',
  },
  {
    title: 'Edición Digital',
    editionLabel: 'Digital Edition',
    priceNumber: 17,
    priceText: '$17',
    thumbnailUrl: '',
  },
]

export function useProductScanner() {
  const [products, setProducts] = useState<Product[]>([])

  useEffect(() => {
    // Wait for DOM to be fully rendered
    const scanTimeout = setTimeout(() => {
      const scannedProducts = scanPageForProducts()

      if (scannedProducts.length > 0) {
        setProducts(scannedProducts)
      } else {
        setProducts(FALLBACK_PRODUCTS)
      }
    }, 500)

    return () => clearTimeout(scanTimeout)
  }, [])

  return products
}

function scanPageForProducts(): Product[] {
  const products: Product[] = []

  // Strategy 1: Check for JSON-LD structured data
  const jsonLdScripts = document.querySelectorAll(
    'script[type="application/ld+json"]',
  )
  jsonLdScripts.forEach((script) => {
    try {
      const data = JSON.parse(script.textContent || '')
      if (data['@type'] === 'Product' && data.offers) {
        products.push({
          title: data.name || '',
          editionLabel: data.offers.name || 'Digital Access',
          priceNumber: parseFloat(data.offers.price) || 0,
          priceText: `$${data.offers.price}`,
          thumbnailUrl: data.image || '',
        })
      }
    } catch (e) {
      // Invalid JSON, skip
    }
  })

  // Strategy 2: Scan for price patterns near CTA buttons
  const ctaKeywords = [
    'Download',
    'Buy',
    'Add to Cart',
    'Checkout',
    'Order',
    'Comprar',
    'Get',
    'Purchase',
  ]
  const priceRegex = /\$\s?(\d+(?:\.\d{2})?)/g

  // Find all buttons and links with CTA text
  const ctaElements = Array.from(document.querySelectorAll('button, a')).filter(
    (el) => {
      const text = el.textContent || ''
      return ctaKeywords.some((keyword) => text.includes(keyword))
    },
  )

  ctaElements.forEach((cta) => {
    // Look for price in nearby elements (parent, siblings, or within same section)
    const section = cta.closest('section, div[class*="rounded"]')
    if (!section) return

    const sectionText = section.textContent || ''
    const priceMatches = Array.from(sectionText.matchAll(priceRegex))

    if (priceMatches.length > 0) {
      // Find product title (look for headings or bold text near the price)
      const titleElements = section.querySelectorAll(
        'h1, h2, h3, h4, span[class*="font-bold"], span[class*="font-semibold"], p[class*="font-bold"]',
      )
      const title =
        Array.from(titleElements)
          .find((el) => {
            const text = el.textContent || ''
            return text.length > 5 && text.length < 100 && !text.includes('$')
          })
          ?.textContent?.trim() || 'Product'

      // Find thumbnail image
      const img = section.querySelector('img')
      const thumbnailUrl = img?.src || ''

      // Extract first price found
      const priceText = priceMatches[0][0]
      const priceNumber = parseFloat(priceMatches[0][1])

      // Check if this product is already added (avoid duplicates)
      const isDuplicate = products.some(
        (p) => p.title === title && p.priceNumber === priceNumber,
      )
      if (!isDuplicate) {
        products.push({
          title,
          editionLabel: 'Digital Access',
          priceNumber,
          priceText,
          thumbnailUrl,
        })
      }
    }
  })

  // Strategy 3: Specific scan for known product images on this page
  const knownProducts = [
    {
      imgSrc: 'The-Cortisol-Switch-Logo.png',
      title: 'The Cortisol Switch',
      editionLabel: 'Complete eBook',
      priceNumber: 6.39,
      priceText: '$6.39',
    },
    {
      imgSrc: 'The-Cortisol-Elite-Motion-Bible.png',
      title: 'The Cortisol-Safe Elite Motion Bible',
      editionLabel: 'Desk Resets + At-Home Sculpt',
      priceNumber: 9,
      priceText: '$9',
    },
    {
      imgSrc: 'Stress-Proof-Dining-Out-Guide.png',
      title: 'Stress-Proof Dining Out Guide',
      editionLabel: 'Safe Orders + Social Buffer',
      priceNumber: 9,
      priceText: '$9',
    },
  ]

  knownProducts.forEach((known) => {
    const img = document.querySelector(
      `img[src*="${known.imgSrc}"]`,
    ) as HTMLImageElement
    if (img) {
      const isDuplicate = products.some((p) => p.title === known.title)
      if (!isDuplicate) {
        products.push({
          title: known.title,
          editionLabel: known.editionLabel,
          priceNumber: known.priceNumber,
          priceText: known.priceText,
          thumbnailUrl: img.src,
        })
      }
    }
  })

  return products
}
